import { useEffect } from "react";
import { CommentModel } from "@/types";
import { filterCommentsByTime } from "@/utils/commentFilter";

/**
 * プレーヤーの時間とコメントを同期するカスタムフック
 * @param playerInstance プレーヤーインスタンス
 * @param allComments 全コメントデータ
 * @param startAt 配信開始時刻
 * @param scrollEnabled スクロール機能が有効かどうか
 * @param setFilteredComments フィルタリング後のコメントを設定する関数
 */
export const usePlayerTimeSync = (
  playerInstance: any,
  allComments: CommentModel[],
  startAt: string,
  scrollEnabled: boolean,
  setFilteredComments: (comments: CommentModel[]) => void
) => {
  useEffect(() => {
    if (!playerInstance || !scrollEnabled || !allComments.length || !startAt) {
      return;
    }

    const updateComments = () => {
      try {
        const currentTime = playerInstance.currentTime();
        if (typeof currentTime === 'number') {
          const filtered = filterCommentsByTime(allComments, startAt, currentTime);
          setFilteredComments(filtered);
        }
      } catch (error) {
        console.warn('Error updating comments:', error);
      }
    };

    // 初回実行
    updateComments();

    // 定期的な更新（2秒間隔でログを削減）
    const interval = setInterval(updateComments, 2000);

    // プレーヤーのイベントでコメントを更新
    const handleTimeUpdate = () => {
      updateComments();
    };

    const handleSeeked = () => {
      // シーク完了時に即座に更新
      updateComments();
    };

    const handleSeeking = () => {
      // シーク中も更新（リアルタイム感を向上）
      updateComments();
    };

    try {
      // 通常の時間更新
      playerInstance.on('timeupdate', handleTimeUpdate);
      // シーク操作時の更新
      playerInstance.on('seeked', handleSeeked);
      playerInstance.on('seeking', handleSeeking);
      // 再生開始時の更新
      playerInstance.on('play', handleTimeUpdate);
      // 一時停止時の更新
      playerInstance.on('pause', handleTimeUpdate);
    } catch (error) {
      console.warn('Failed to bind player events:', error);
    }

    return () => {
      clearInterval(interval);
      try {
        playerInstance.off('timeupdate', handleTimeUpdate);
        playerInstance.off('seeked', handleSeeked);
        playerInstance.off('seeking', handleSeeking);
        playerInstance.off('play', handleTimeUpdate);
        playerInstance.off('pause', handleTimeUpdate);
      } catch (error) {
        console.warn('Failed to unbind player events:', error);
      }
    };
  }, [playerInstance, allComments, startAt, scrollEnabled]);
};
